import cn from 'classnames';
import * as React from 'react';
import { useState } from 'react';
import { Search, PreferencesApi } from '@weddingspot/ws-api-client';
import { LocalStorage, StorableDataKeys, WSAppContext } from '@weddingspot/ws-core-components';
import * as analytics from '@weddingspot/ws-analytics';
import { useFrontendPreferences } from './hooks';

const { AnalyticsEvent, logEvent } = analytics;

import { Button } from '@cvent/carina/components/Button';
import { XIcon, HomeIcon, UtencilsIcon, DollarSignIcon } from '@cvent/carina/components/Icon';
import { Row } from '@cvent/carina/components/Row';
import { Col } from '@cvent/carina/components/Col';

const _styles = require('./BudgetBannerListView.scss');
const budgetBannerRings = require('../../assets/images/budget-banner-rings.svg');

export interface BudgetBannerProps {
    venue?: Search.SearchResult | null;
}

export const BudgetBannerListView = React.memo((props: BudgetBannerProps) => {
    const [isHidden, setIsHidden] = useState(true);
    const { userIsAuthenticated, user, openAuthModal } = React.useContext(WSAppContext);
    const frontendPreferences = useFrontendPreferences();

    React.useEffect(() => {
        let bannerSeen;
        if (userIsAuthenticated && frontendPreferences) {
            const pref = frontendPreferences.find((pref) => pref.key === '14');
            bannerSeen = pref ? Boolean(pref.value) : false;
        } else {
            bannerSeen = LocalStorage.getItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN);
        }

        if (!bannerSeen) {
            setIsHidden(false);
            LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, false);
        } else {
            LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, true);
        }
    }, []);

    const setFrontendPreference = () => {
        const data = {
            key: '14',
            is_active: true,
            value: 1,
            description: 'Is Budget Estimate Banner Seen',
            user_id: user?.id,
        };
        PreferencesApi.update(data).then((response) => {});
    };

    const handleCTAClick = (e: React.SyntheticEvent) => {
        LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, true);
        setIsHidden(true);
        if (userIsAuthenticated) {
            setFrontendPreference();
        }
        logEvent(AnalyticsEvent.EVTNM_BUDGET_ESTIMATE_BANNER_SEEN, {});
    };

    const loginText = () => {
        return (
            <>
                To create an estimate for more than 1 venue,{' '}
                <a
                    className={_styles.loginLink}
                    href='#'
                    onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                        openAuthModal();
                    }}
                >
                    log in or create an account.
                </a>
            </>
        );
    };

    return (
        <div className={cn(_styles.wrapper, _styles.wrapper_list, { [_styles.hidden]: isHidden})}>
            <Row>
                <Col width='content'>
                    <div className={_styles.cardImage}>
                        <picture>
                            <img src={budgetBannerRings} alt={`Budget Estimate Banner`} />
                        </picture>
                    </div>
                </Col>
                <Col width='fill'>
                    <div className={_styles.titleRow}>
                        <div className={_styles.titleLeft}>
                            <div className={cn(_styles.title)}>Budget with Spot Estimate</div>
                        </div>
                        <div className={_styles.titleRight}>
                            <Button icon={XIcon} onClick={handleCTAClick} size='s' appearance='ghost' variant='neutral-black' aria-label="Close"/>
                        </div>
                    </div>
                    <div className={cn(_styles.description)}>
                        Our <b>Spot Estimate</b> tool helps calculate estimated costs so you can stay within budget. In addition to
                        providing a starting price, Spot Estimate also shows:
                    </div>
                    <div className={cn(_styles.includesInfo)}>
                        <ul>
                            <li>
                                <div className={_styles.icons}>
                                    <HomeIcon />
                                </div>
                                <span>Venue Rental Costs</span>
                            </li>
                            <li>
                                <div className={_styles.icons}>
                                    <UtencilsIcon />
                                </div>
                                <span>Food & Beverage Costs</span>
                            </li>
                            <li>
                                <div className={_styles.icons}>
                                    <DollarSignIcon />
                                </div>
                                <span>Taxes and Other Fees</span>
                            </li>
                        </ul>
                    </div>
                    <div className={cn(_styles.description)}>
                        Try it by clicking on "Price Venue" for your favorite venue. {!!!userIsAuthenticated && loginText()}
                    </div>
                </Col>
            </Row>
        </div>
    );
});
